import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Button, Tab, Tabs } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  PROGRAM_UNIT_LABELS,
  commitmentsAt,
} from 'common/src/utils/commercialProgram';
import { useTranslation } from 'common/src/hooks/useTranslation';
import {
  Alert,
  EmptyState,
  PageHeader,
  LoadingState,
  PageLayout,
} from '../../components/ui';
import { useCommercialProgram } from './useCommercialProgram';
import ProgramToolbar, { CURRENT_VERSION } from './ProgramToolbar';
import ProgramSettingsModal from './ProgramSettingsModal';
import SummaryView from './SummaryView';
import DetailView from './DetailView';

type View = 'summary' | 'detail';

// Resumen y Gestionar compromisos son rutas distintas: la versión, los
// clientes elegidos y los agregados al programa se recuerdan al cambiar de
// pestaña.
const lastSelection = {
  versionId: CURRENT_VERSION,
  receiverIds: new Set<string>(),
  addedReceiverIds: new Set<number>(),
};

export const CommercialPlanSummaryPage = () => (
  <CommercialPlanPage view='summary' />
);
export const CommercialPlanDetailPage = () => (
  <CommercialPlanPage view='detail' />
);

/** Programa Comercial (ex Compromisos): Resumen y Gestionar compromisos. */
export default function CommercialPlanPage({
  view = 'summary',
}: {
  view?: View;
}) {
  const { t } = useTranslation('commitments');
  const [, navigate] = useLocation();
  const state = useCommercialProgram();
  const {
    loading,
    program,
    rows,
    receivers,
    unknownReceivers,
    season,
    species,
    userProfile,
    reloadProgram,
  } = state;
  const [selectedReceiverIds, setSelectedReceiverIds] = useState<Set<string>>(
    lastSelection.receiverIds
  );
  // Clientes agregados al programa que todavía no tienen compromisos.
  const [addedReceiverIds, setAddedReceiverIds] = useState<Set<number>>(
    lastSelection.addedReceiverIds
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Programa que se ve en las dos pestañas: el vigente o una versión.
  const [versionId, setVersionId] = useState(lastSelection.versionId);
  lastSelection.versionId = versionId;
  lastSelection.receiverIds = selectedReceiverIds;
  lastSelection.addedReceiverIds = addedReceiverIds;
  const version =
    state.versions.find((v) => String(v.id) === versionId) || null;
  // Quien llega a esta página tiene el permiso del Programa Comercial.
  const canEdit = true;

  // Gestionar compromisos muestra solo los clientes del programa: los que
  // tienen compromisos en la versión que se ve, más los recién agregados. El
  // Resumen ya oculta los clientes sin compromisos ni despachos.
  const programReceiverIds = useMemo(() => {
    const ids = new Set(addedReceiverIds);
    commitmentsAt(rows, version?.as_of).forEach((row) => {
      if (Number(row.quantity) > 0) ids.add(row.exporter_receiver_id);
    });
    return ids;
  }, [rows, version, addedReceiverIds]);
  const viewReceivers =
    view === 'detail'
      ? receivers.filter((r) => programReceiverIds.has(r.id))
      : receivers;
  const addToProgram = (receiverId: number) =>
    setAddedReceiverIds((prev) => new Set(prev).add(receiverId));

  // Sin selección = todos los clientes.
  const filteredReceivers =
    selectedReceiverIds.size === 0
      ? viewReceivers
      : viewReceivers.filter((r) => selectedReceiverIds.has(String(r.id)));

  // Algunos compromisos vigentes, para la vista previa al cambiar de unidad.
  const previewRows = useMemo(() => {
    const nameOf = new Map(receivers.map((r) => [r.id, r.name]));
    return commitmentsAt(rows)
      .filter((row) => Number(row.quantity) > 0)
      .slice(0, 3)
      .map((row) => ({
        label: `${nameOf.get(row.exporter_receiver_id) ?? ''} S${row.week}`,
        quantity: Number(row.quantity),
      }));
  }, [rows, receivers]);

  const content = () => {
    if (!species?.id) {
      return (
        <EmptyState
          icon='tabler:plant-2'
          title={t('Selecciona una especie para ver y editar el plan.')}
        />
      );
    }
    if (loading) return <LoadingState label={t('Cargando...')} />;
    if (!program) {
      return (
        <EmptyState
          icon='tabler:calendar-stats'
          title={t(
            'Todavía no hay un programa comercial para {{species}} {{season}}',
            { species: species.name, season }
          )}
          description={t(
            'Elige en qué unidad lo vas a llevar (kilos, cajas equivalentes o embarques) y la fecha base de las semanas.'
          )}
          action={
            canEdit && (
              <Button color='primary' onPress={() => setSettingsOpen(true)}>
                {t('Crear programa')}
              </Button>
            )
          }
        />
      );
    }
    return (
      <>
        <ProgramToolbar
          state={state}
          program={program}
          canEdit={canEdit}
          canSaveVersion={view === 'detail'}
          versionId={versionId}
          onVersionChange={setVersionId}
          receivers={viewReceivers}
          selectedReceiverIds={selectedReceiverIds}
          onReceiversChange={setSelectedReceiverIds}
        />
        {view === 'detail' ? (
          <DetailView
            state={state}
            program={program}
            receivers={filteredReceivers}
            addableReceivers={receivers.filter(
              (r) => !programReceiverIds.has(r.id)
            )}
            onAddReceiver={addToProgram}
            canEdit={canEdit}
            version={version}
          />
        ) : (
          <SummaryView
            state={state}
            program={program}
            receivers={filteredReceivers}
            version={version}
          />
        )}
      </>
    );
  };

  return (
    <PageLayout className='h-screen overflow-y-auto'>
      <PageHeader
        title={t('Programa Comercial')}
        description={
          <span className='flex flex-wrap items-center gap-x-2 text-sm'>
            <span>
              {[species?.name, season].filter(Boolean).join(' · ')}
              {program &&
                ` · ${t(PROGRAM_UNIT_LABELS[program.unit])} · ${
                  program.date_basis === 'eta'
                    ? t('semanas de arribo (ETA)')
                    : t('semanas de despacho (ETD)')
                }`}
            </span>
            {program && canEdit && (
              <button
                type='button'
                onClick={() => setSettingsOpen(true)}
                className='inline-flex items-center gap-1 text-primary hover:underline'
              >
                <Icon icon='tabler:settings' width={14} />
                {t('Configurar')}
              </button>
            )}
          </span>
        }
        actions={
          <Tabs
            selectedKey={view}
            onSelectionChange={(key) => navigate(`/commercial-plan/${key}`)}
            aria-label={t('Vistas')}
            variant='underlined'
          >
            <Tab key='summary' title={t('Resumen')} />
            <Tab key='detail' title={t('Gestionar compromisos')} />
          </Tabs>
        }
      />

      {unknownReceivers.length > 0 && (
        <Alert variant='danger' className='mb-4'>
          <a
            href='/settings'
            className='font-medium underline underline-offset-2'
          >
            {t(
              'Hay {{count}} cliente(s) en Despachos sin configurar en Recibidores: {{list}}. Click para configurarlos.',
              {
                count: unknownReceivers.length,
                list: unknownReceivers.join(', '),
              }
            )}
          </a>
        </Alert>
      )}

      {content()}

      {species?.id && userProfile?.exporter_id && (
        <ProgramSettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          program={program}
          exporterId={userProfile.exporter_id}
          season={season}
          speciesId={species.id}
          defaultKgPerCeq={Number(species.default_unit_price) || 5}
          userId={userProfile.id}
          previewRows={previewRows}
          onSaved={reloadProgram}
        />
      )}
    </PageLayout>
  );
}
